import type {
  AdminApiAiUsage,
  AdminApiAiUsageByFeature,
} from "@repo/app-types/entities/aiCreditUsage";
import {
  MetronomeErrorCode,
  metronomeErrorResponseSchema,
  resolvePeriodCreditCapacity,
  type MetronomeCoverage,
} from "@repo/app-types/entities/metronome";
import {
  USAGE_BASED_FEATURE_UNIT_NOUNS,
  getAiFeatureDefinition,
} from "@repo/app-types/helpers/aiFeatureCatalog";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { DefaultSkeleton, TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Size } from "@repo/ui/helpers/helpers";
import { IconCalendar } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { Fragment, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useAiUsageQuery } from "../../../api/entities/company.js";
import { isFetchBaseQueryError } from "../../../api/helpers/errors.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  formatPeriodLabel,
  formatPeriodResetDate,
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

/**
 * The AI usage section of the redesigned billing page: period picker, usage
 * bar against Metronome's current-period credit budget, and the per-feature
 * breakdown.
 * Usage-only preview — nothing here bills, alerts, or pauses features.
 */
export const AiCreditsSection: FC = () => {
  const [periodOffset, setPeriodOffset] = useState(0);
  // The current-period query doubles as the section shell (period list), so
  // switching periods re-renders the body without blanking the picker.
  const { currentData: currentUsage } = useAiUsageQuery({ periodOffset: 0 });
  // `currentData` is intentionally argument-specific. RTK Query's `data`
  // falls back to the previous argument's result while a new period loads,
  // which would make stale usage look like it belongs to the selected period.
  const {
    currentData: selectedUsage,
    error: usageQueryError,
    isError: isUsageError,
    isFetching,
  } = useAiUsageQuery({ periodOffset });
  const parsedUsageError = isFetchBaseQueryError(usageQueryError)
    ? metronomeErrorResponseSchema.safeParse(usageQueryError.data)
    : null;
  const usageErrorCode = parsedUsageError?.success
    ? parsedUsageError.data.errorCode
    : null;
  const isUsageLoading =
    !isUsageError && (isFetching || selectedUsage === undefined);

  const periodOptions: readonly SimpleSelectOption<number>[] =
    currentUsage?.periods.map((period) => ({
      value: period.offset,
      label: formatPeriodLabel(period),
    })) ?? [];

  return (
    <section>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <h2 className="text-base font-semibold text-foreground">
          AI usage and credits
        </h2>
        {currentUsage ? (
          <SimpleSelect
            isLabelHidden
            label="Usage period"
            leftAccessory={<IconCalendar className="size-4" />}
            onChange={setPeriodOffset}
            options={periodOptions}
            size={Size.SMALL}
            value={periodOffset}
          />
        ) : null}
      </div>

      <div className="mt-5">
        <InfoBanner
          title="Usage preview"
          description="Usage is tracked, but nothing is billed or paused based on it."
        />
      </div>

      <div className="mt-5">
        {usageErrorCode === MetronomeErrorCode.NOT_FOUND ? (
          <NoBillingData />
        ) : usageErrorCode === MetronomeErrorCode.METRONOME_UNAVAILABLE ? (
          <div role="alert">
            <InfoBanner
              title="Billing is unavailable"
              description="Try again later."
              variant={SimpleModalVariant.Warning}
            />
          </div>
        ) : isUsageError ? (
          <div role="alert">
            <InfoBanner
              title="Unable to load AI usage"
              description="Refresh the page to try again."
              variant={SimpleModalVariant.Warning}
            />
          </div>
        ) : isUsageLoading ? (
          <div className="rounded-md border border-border-light bg-white px-5 py-4">
            <DefaultSkeleton />
          </div>
        ) : selectedUsage ? (
          <UsageOverview usage={selectedUsage} />
        ) : null}
      </div>
      {isUsageError ? null : (
        <div className="mt-4">
          <UsageBreakdown
            features={selectedUsage?.features ?? []}
            isLoading={isUsageLoading}
          />
        </div>
      )}
    </section>
  );
};

/**
 * Metronome metered nothing for this scope: a company whose billing is not set
 * up yet, or a period that closed before the company was on a Metronome
 * contract. Neither is a failure, so neither is announced as one — an
 * unreadable Metronome is the alert above.
 */
const NoBillingData: FC = () => (
  <div
    role="status"
    className="rounded-md border border-border-light bg-white px-5 py-4 text-sm text-muted-foreground"
  >
    No AI usage for this period
  </div>
);

interface UsageOverviewProps {
  usage: AdminApiAiUsage;
}

/** What the bar measures, against what it is measured out of. */
interface UsageBarReading {
  consumed: number;
  capacity: number;
  /** How the figures beneath the bar name what it counts. */
  caption: string;
}

/**
 * The bar's two figures. A period with live balances paces every credit it
 * can still spend, because that is the question while it is running. A closed
 * one measures the recurring grant alone: packs and promotional credits are
 * pools spanning many periods, so dividing by them would move the bar as a
 * pool drains rather than as usage happens. What the pools paid for, and what
 * was left of them, are named beside it instead.
 *
 * The grant draws down before any pack does, so a closed period's bar reaches
 * 100% exactly when the allowance ran out and never overshoots it.
 */
function resolveUsageBar(usage: AdminApiAiUsage): UsageBarReading | null {
  const capacity = resolvePeriodCreditCapacity(usage);
  if (capacity !== null) {
    return capacity > 0
      ? {
          consumed: usage.credits_used,
          capacity,
          caption: "credits used",
        }
      : null;
  }
  return usage.included_ai_credits !== null && usage.included_ai_credits > 0
    ? {
        consumed: usage.coverage.from_included_credits,
        capacity: usage.included_ai_credits,
        caption: "included credits used",
      }
    : null;
}

/** The card's heading and the figure opposite it. */
function describeAllowance(usage: AdminApiAiUsage): {
  title: string;
  headline: string;
} {
  return usage.balances.kind === "live"
    ? {
        title: "Monthly AI credits",
        headline: `${formatWholeNumber(usage.balances.remaining_ai_credits)} left`,
      }
    : {
        title: "AI credits used",
        headline: formatWholeNumber(usage.credits_used),
      };
}

/**
 * What paid for the usage besides the recurring grant. Metronome names the
 * credit behind every line on the statement, so a closed period reports this
 * exactly — no balance and no reconstruction involved. The grant is the bar's
 * own subject, so only the other sources are named here.
 */
function formatCoverage(coverage: MetronomeCoverage): string[] {
  return [
    coverage.from_purchased_credits > 0
      ? `${formatWholeNumber(coverage.from_purchased_credits)} covered by purchased credits`
      : null,
    coverage.from_promotional_credits > 0
      ? `${formatWholeNumber(coverage.from_promotional_credits)} covered by promotional credits`
      : null,
    coverage.from_other_credits > 0
      ? `${formatWholeNumber(coverage.from_other_credits)} covered by other credits`
      : null,
  ].filter((part) => part !== null);
}

const UsageOverview: FC<UsageOverviewProps> = (props) => {
  const { usage } = props;
  const { balances } = usage;

  const isCurrentPeriod = usage.selected_period.offset === 0;
  const usageBar = resolveUsageBar(usage);
  const { title, headline } = describeAllowance(usage);
  const projectedCredits = isCurrentPeriod ? projectPeriodTotal(usage) : null;

  const details = [
    usageBar === null
      ? `${formatWholeNumber(usage.credits_used)} credits used`
      : formatUsageAgainstIncluded(
          usageBar.consumed,
          usageBar.capacity,
          usageBar.caption,
        ),
    balances.kind === "live" && balances.remaining_purchased_credits > 0
      ? usage.included_ai_credits !== null
        ? `${formatWholeNumber(usage.included_ai_credits)} included + ${formatWholeNumber(balances.remaining_purchased_credits)} purchased left`
        : `${formatWholeNumber(balances.remaining_purchased_credits)} purchased credits left`
      : null,
    ...formatCoverage(usage.coverage),
    balances.kind === "at_period_close" &&
    balances.remaining_purchased_credits > 0
      ? `${formatWholeNumber(balances.remaining_purchased_credits)} purchased credits left at period close`
      : null,
    usage.credits_overage > 0
      ? `${formatWholeNumber(usage.credits_overage)} uncovered`
      : null,
    isCurrentPeriod
      ? `Resets ${formatPeriodResetDate(usage.selected_period)}`
      : formatPeriodLabel(usage.selected_period),
  ].filter((detail) => detail !== null);

  return (
    <section className="rounded-md border border-border-light bg-white px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="shrink-0 text-sm font-semibold text-foreground tabular-nums">
          {headline}
        </p>
      </div>

      {usageBar ? (
        <UsageBar
          className="mt-3 w-full"
          percentage={(usageBar.consumed / usageBar.capacity) * 100}
          isUncovered={usage.credits_overage > 0}
        />
      ) : null}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <p>
          {details.map((detail, index) => (
            <Fragment key={detail}>
              {index > 0 ? <span aria-hidden="true"> · </span> : null}
              {detail}
            </Fragment>
          ))}
        </p>
        <p className="text-right">
          {isCurrentPeriod
            ? projectedCredits !== null
              ? `Projected: ${formatWholeNumber(projectedCredits)} used by reset`
              : `${formatWholeNumber(usage.audio_minutes_used)} audio minutes used`
            : "Period ended"}
        </p>
      </div>
    </section>
  );
};

/**
 * Linear projection of the period's final credit total from the fraction of
 * the period elapsed. Null while so little has elapsed that the extrapolation
 * would be noise.
 */
function projectPeriodTotal(usage: AdminApiAiUsage): number | null {
  const start = DateTime.fromISO(usage.selected_period.starts_at).toMillis();
  const end = DateTime.fromISO(usage.selected_period.ends_at).toMillis();
  const elapsedFraction = (Date.now() - start) / (end - start);

  if (elapsedFraction < 0.03 || elapsedFraction > 1) {
    return null;
  }
  return usage.credits_used / elapsedFraction;
}

interface UsageBreakdownProps {
  features: readonly AdminApiAiUsageByFeature[];
  isLoading: boolean;
}

const UsageBreakdown: FC<UsageBreakdownProps> = (props) => {
  const { features: rows, isLoading } = props;

  return (
    <section className="space-y-4">
      <h2 className="text-base font-semibold text-foreground">
        Usage by feature
      </h2>
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-[45%]">
              Feature
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[30%]">
              Volume
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[25%] text-right">
              Credits used
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>
          {isLoading ? <TableSkeleton /> : null}
          {rows.map((row) => (
            <tr className="hover:bg-gray-50" key={row.feature_key}>
              <SettingsTableDataCell>
                {getAiFeatureDefinition(row.feature_key)?.label ??
                  row.feature_key}
              </SettingsTableDataCell>
              <SettingsTableDataCell>
                {formatFeatureUnitCount(row)}
              </SettingsTableDataCell>
              <SettingsTableDataCell className="text-right tabular-nums">
                {formatWholeNumber(row.credits_used)}
              </SettingsTableDataCell>
            </tr>
          ))}
        </SettingsTableBodySection>
      </SettingsTable>
    </section>
  );
};

function formatFeatureUnitCount(feature: AdminApiAiUsageByFeature): string {
  const featureUnitNoun = USAGE_BASED_FEATURE_UNIT_NOUNS[feature.feature_key];
  const noun =
    feature.feature_unit_count === 1
      ? featureUnitNoun.singular
      : featureUnitNoun.plural;
  return `${formatWholeNumber(feature.feature_unit_count)} ${noun}`;
}

interface UsageBarProps {
  percentage: number;
  /**
   * Whether Metronome left any of this period's usage uncovered. A closed
   * period can pass 100% of its included grant with a purchased pack having
   * covered the rest, so the alarm state follows the overage figure rather
   * than the bar's own fill.
   */
  isUncovered: boolean;
  className?: string;
}

/** Credits consumed against the period's spend capacity. */
const UsageBar: FC<UsageBarProps> = (props) => {
  const { percentage, isUncovered, className } = props;

  const fillColor = isUncovered
    ? "bg-red-500"
    : percentage >= 80
      ? "bg-amber-500"
      : "bg-indigo-600";

  return (
    <div
      aria-label={`${Math.round(percentage)}% of available credits used`}
      className={twMerge(
        "h-2 overflow-hidden rounded-full bg-gray-100",
        className,
      )}
      role="meter"
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.min(100, Math.round(percentage))}
    >
      <div
        className={twMerge(
          "h-full rounded-full transition-all duration-300",
          fillColor,
        )}
        style={{ width: `${Math.min(100, percentage)}%` }}
      />
    </div>
  );
};
